import {
  cookDateSavedMessage,
  formatScaleFactor,
  formatServingsLabel,
  formatCookDay,
  summariseCookDates,
  takePendingScheduleDate,
  nutritionBasisLabel,
  RECIPE_TEXT_SIZES,
  scaleIngredientAmount,
  scaleServings,
  type NutritionFact,
} from '@platewell/core';
import {
  listContextPages,
  listContextParams,
  listContextQuery,
  parseListContext,
  useCollections,
  useConnectionStatus,
  useFormatTag,
  useListContextLabel,
  useRecipe,
  useRecipeSummaries,
  useStore,
} from '@platewell/query';
import { Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useState, type CSSProperties } from 'react';
import { BackToListLink, backToListTarget } from '../components/back-to-list-link.js';
import { CookDateDialog } from '../components/cook-date-dialog.js';
import { CookHistoryDialog } from '../components/cook-history-dialog.js';
import { useShowToast } from '../components/toast.js';
import { FavouriteButton } from '../components/favourite-button.js';
import { MoreMenu } from '../components/more-menu.js';
import { Photo, RecipeHeroPhoto } from '../components/photo.js';
import { PhotoLightbox } from '../components/photo-lightbox.js';
import { formatMinutes } from '../components/recipe-card.js';
import { RevisionDrawer } from '../components/revision-drawer.js';
import { ScaleSwitch } from '../components/scale-switch.js';
import { ShareDialog } from '../components/share-dialog.js';
import { quotaRefusal, UpgradeNotice, type QuotaRefusal } from '../components/upgrade-notice.js';
import { useMediaQuery } from '../hooks/use-media-query.js';
import { useRecipeTextSize } from '../hooks/use-recipe-text-size.js';
import { useWakeLock } from '../hooks/use-wake-lock.js';
import { recipeRoute } from '../router.js';

export function RecipeDetail() {
  const { recipeId } = recipeRoute.useParams();
  const search = recipeRoute.useSearch();
  const recipe = useRecipe(recipeId);
  const collections = useCollections();
  const { readOnly } = useConnectionStatus();
  const formatTag = useFormatTag();
  const store = useStore();
  const navigate = useNavigate();
  const [showHistory, setShowHistory] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [showCookDates, setShowCookDates] = useState(false);
  const [showCookHistory, setShowCookHistory] = useState(false);
  // Pager navigation keeps this component mounted, so Cook Mode stays on from recipe to recipe
  // and ends only when the user leaves the recipe view.
  const [cookMode, setCookMode] = useState(false);
  const wakeLock = useWakeLock(cookMode);
  const [textSizeIndex, increaseTextSize] = useRecipeTextSize();
  const textSize = RECIPE_TEXT_SIZES[textSizeIndex]!;
  // Scaling only changes this view, never the saved recipe. Unlike Cook Mode it belongs to one
  // recipe, so paging to the next one shows that recipe as written.
  const [scale, setScale] = useState({ recipeId, factor: 1, open: false });
  const scaleFactor = scale.recipeId === recipeId ? scale.factor : 1;
  const scaleOpen = scale.recipeId === recipeId && scale.open;
  const showToast = useShowToast();
  // Same breakpoint as the phone's tab bar: below it, the less-used actions fold into More.
  const narrow = useMediaQuery('(width <= 900px)');
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [restoreError, setRestoreError] = useState<string | null>(null);
  const [restoreRefusal, setRestoreRefusal] = useState<QuotaRefusal | null>(null);

  // When the user arrived from a list, rebuild that identical ordered list so they can page
  // through it and get back to it. listContextQuery is the single definition the list views
  // themselves use, so the two orders cannot drift. The URL is already stable, so no debounce
  // is needed here.
  const context = parseListContext(search);
  const listLabel = useListContextLabel(context);
  const pagerSearch = context == null ? {} : listContextParams(context);
  const results = useRecipeSummaries(context == null ? { limit: 0 } : listContextQuery(context));
  const resultIndex = context == null ? -1 : results.findIndex((r) => r.id === recipeId);
  // The back link stays even when the recipe has dropped out of its own list — losing the way
  // out at exactly that moment is the worst time for it to disappear.
  const showBack = context != null && listLabel != null;
  const showPager =
    context != null && listContextPages(context) && resultIndex !== -1 && results.length > 1;
  const prevResult = showPager ? results[resultIndex - 1] : undefined;
  const nextResult = showPager ? results[resultIndex + 1] : undefined;

  useEffect(() => {
    if (!showBack || context == null) return;
    function onKeyDown(event: KeyboardEvent) {
      if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) return;
      const target = event.target as HTMLElement | null;
      if (
        target != null &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT' ||
          target.isContentEditable)
      ) {
        return;
      }
      // useEscapeToClose binds Escape on the window too and does not stop propagation, and this
      // listener is registered first because the page outlives every overlay. So suppressing
      // here is the only way to stop Escape both closing a dialog and navigating away.
      if (lightboxIndex != null || showHistory || showShare) return;
      if (document.querySelector('[role="dialog"], [role="menu"]') != null) return;
      if (event.key === 'Escape' || event.key === 'u' || event.key === 'U') {
        event.preventDefault();
        void navigate(backToListTarget(context!));
        return;
      }
      if (!showPager) return;
      const neighbor =
        event.key === 'ArrowLeft' || event.key === 'k' || event.key === 'K'
          ? prevResult
          : event.key === 'ArrowRight' || event.key === 'j' || event.key === 'J'
            ? nextResult
            : undefined;
      if (neighbor == null) return;
      event.preventDefault();
      void navigate({
        to: '/recipes/$recipeId',
        params: { recipeId: neighbor.id },
        search: pagerSearch,
      });
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [
    showBack,
    showPager,
    lightboxIndex,
    showHistory,
    showShare,
    prevResult?.id,
    nextResult?.id,
    search.q,
    search.tag,
    search.collectionId,
    search.from,
    navigate,
  ]);

  if (recipe == null) {
    return (
      <div className="empty">
        <h2>Recipe not found</h2>
        <p className="hint">It may have been deleted on another device.</p>
        {showBack && context != null ? (
          <BackToListLink context={context} label={listLabel!} className="btn" />
        ) : (
          <Link className="btn" to="/">
            Back to all recipes
          </Link>
        )}
      </div>
    );
  }

  const inCollections = collections.filter((collection) =>
    recipe.collectionIds.includes(collection.id),
  );
  const cookSummary = summariseCookDates(recipe.cookDates);
  const scaled = scaleFactor !== 1;
  const scaledServings = scaled ? scaleServings(recipe.servings, scaleFactor) : null;

  function onCookDateClick() {
    if (readOnly) {
      setShowCookDates(true);
      return;
    }
    // Arriving from a day on the schedule means the day is already chosen — log it straight
    // away rather than making the user find it again in the dialog. The toast is the only
    // sign anything happened, since no dialog ever opens.
    const pending = takePendingScheduleDate();
    if (pending != null) {
      void store.addCookDate(recipeId, pending, scaleFactor);
      showToast(cookDateSavedMessage(pending));
      return;
    }
    setShowCookDates(true);
  }

  return (
    <>
      {showBack && context != null && (
        <nav className="result-pager" aria-label="Recipe list">
          <BackToListLink context={context} label={listLabel!} />
          {showPager && (
            <>
              <span className="hint">
                {resultIndex + 1} of {results.length}
              </span>
              {prevResult != null ? (
                <Link
                  className="result-pager-arrow"
                  to="/recipes/$recipeId"
                  params={{ recipeId: prevResult.id }}
                  search={pagerSearch}
                  aria-label="Previous result"
                >
                  ‹ Prev
                </Link>
              ) : (
                <span className="result-pager-arrow" aria-disabled="true">
                  ‹ Prev
                </span>
              )}
              {nextResult != null ? (
                <Link
                  className="result-pager-arrow"
                  to="/recipes/$recipeId"
                  params={{ recipeId: nextResult.id }}
                  search={pagerSearch}
                  aria-label="Next result"
                >
                  Next ›
                </Link>
              ) : (
                <span className="result-pager-arrow" aria-disabled="true">
                  Next ›
                </span>
              )}
            </>
          )}
        </nav>
      )}

      <div className="row wrap recipe-header">
        <h1 className="recipe-title">{recipe.title}</h1>
        <span className="spacer" />
        <FavouriteButton recipeId={recipeId} className="btn favourite-detail-button" />
        <button
          type="button"
          className="btn"
          aria-haspopup="dialog"
          aria-expanded={showCookDates}
          onClick={onCookDateClick}
        >
          Cook Dates
        </button>
        {wakeLock.supported && (
          <button
            type="button"
            className="btn cook-mode-toggle"
            role="switch"
            aria-checked={cookMode}
            title="Keep the screen awake while you cook"
            onClick={() => setCookMode((value) => !value)}
          >
            Cook Mode
          </button>
        )}
        <button
          type="button"
          className={`btn text-size-toggle ${textSizeIndex > 0 ? 'enlarged' : ''}`}
          aria-label="Large text"
          aria-pressed={textSizeIndex > 0}
          title="Make the recipe text larger"
          onClick={increaseTextSize}
        >
          <span aria-hidden="true">
            <span className="text-size-toggle-small">A</span>A
          </span>
        </button>
        {!narrow && (
          <>
            <button className="btn" onClick={() => setShowHistory(true)}>
              History
            </button>
            <button className="btn" onClick={() => setShowShare(true)}>
              Share
            </button>
            <button className="btn" onClick={() => window.print()}>
              Print
            </button>
          </>
        )}
        {!readOnly && (
          <Link
            className="btn"
            to="/recipes/$recipeId/edit"
            params={{ recipeId }}
            search={pagerSearch}
          >
            Edit
          </Link>
        )}
        {narrow && (
          <MoreMenu
            items={[
              { label: 'Share', onSelect: () => setShowShare(true) },
              { label: 'Print', onSelect: () => window.print() },
              { label: 'History', onSelect: () => setShowHistory(true) },
            ]}
          />
        )}
      </div>

      {recipe.deletedAt != null && (
        <div className="empty" style={{ marginBottom: 20, padding: 16 }}>
          <span>
            This recipe is in the trash.{' '}
            {!readOnly && (
              <button
                className="btn ghost"
                onClick={() => {
                  setRestoreError(null);
                  setRestoreRefusal(null);
                  // A restore takes a slot back, so a full household is refused locally.
                  void store.restoreRecipe(recipeId).catch((cause: unknown) => {
                    const quota = quotaRefusal(cause);
                    if (quota != null) setRestoreRefusal(quota);
                    else
                      setRestoreError(
                        cause instanceof Error ? cause.message : 'Could not restore the recipe',
                      );
                  });
                }}
              >
                Restore it
              </button>
            )}
          </span>
          {restoreRefusal != null && (
            <UpgradeNotice
              refusal={restoreRefusal}
              action={'restore this recipe \u2014 or delete one for good to free a slot'}
            />
          )}
          {restoreError != null && <p className="error-text">{restoreError}</p>}
        </div>
      )}

      <div className="recipe" style={{ '--recipe-text-scale': textSize.scale } as CSSProperties}>
        {recipe.photos[0] != null && (
          <div className="recipe-photos">
            <button
              type="button"
              className="recipe-hero-button"
              onClick={() => setLightboxIndex(0)}
              aria-label={
                recipe.photos.length > 1
                  ? `View photos of ${recipe.title}`
                  : `View photo of ${recipe.title}`
              }
            >
              <RecipeHeroPhoto
                blobId={recipe.photos[0].blobId}
                alt={recipe.title}
                width={recipe.photos[0].width}
                height={recipe.photos[0].height}
              />
              {recipe.photos.length > 1 && (
                <span className="recipe-photo-count">{recipe.photos.length} photos</span>
              )}
            </button>
            {recipe.photos.length > 1 && (
              <div className="photo-strip recipe-photo-strip">
                {recipe.photos.map((photo, index) => (
                  <button
                    key={photo.id}
                    type="button"
                    className="photo-thumb recipe-photo-thumb"
                    onClick={() => setLightboxIndex(index)}
                    aria-label={`View photo ${index + 1} of ${recipe.photos.length}`}
                  >
                    <Photo blobId={photo.blobId} alt="" size={400} />
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {recipe.description !== '' && <p className="recipe-description">{recipe.description}</p>}

        <div className="recipe-body">
          <aside className="aside recipe-aside">
            {recipe.servings !== '' && (
              <div className="kv">
                <span>Servings</span>
                {scaled ? (
                  <span className="servings-scaled">
                    <strong>{scaledServings ?? recipe.servings}</strong>
                    <span className="servings-from">
                      {formatScaleFactor(scaleFactor)} of {recipe.servings}
                    </span>
                  </span>
                ) : (
                  <span>{recipe.servings}</span>
                )}
              </div>
            )}
            {recipe.prepMinutes != null && (
              <div className="kv">
                <span>Prep</span>
                <span>{formatMinutes(recipe.prepMinutes)}</span>
              </div>
            )}
            {recipe.cookMinutes != null && (
              <div className="kv">
                <span>Cook</span>
                <span>{formatMinutes(recipe.cookMinutes)}</span>
              </div>
            )}
            {recipe.nutritionFacts.length > 0 && (
              <div className="recipe-nutrition">
                <span className="label">Nutrition</span>
                <p className="hint" style={{ margin: '4px 0 8px' }}>
                  {nutritionBasisLabel(recipe.nutritionBasis)}
                </p>
                {recipe.nutritionFacts.map((fact) => (
                  <div className="kv" key={fact.id}>
                    <span>{fact.label}</span>
                    <span>{formatNutritionAmount(fact)}</span>
                  </div>
                ))}
              </div>
            )}
            {cookSummary.lastMade != null && (
              <div className="kv">
                <span>Last made</span>
                <button
                  type="button"
                  className="kv-button"
                  aria-label={`Last made ${formatCookDay(cookSummary.lastMade)}. Show times made`}
                  onClick={() => setShowCookHistory(true)}
                >
                  {formatCookDay(cookSummary.lastMade)}
                </button>
              </div>
            )}
            {cookSummary.plannedFor != null && (
              <div className="kv">
                <span>Planned for</span>
                <button
                  type="button"
                  className="kv-button"
                  aria-label={`Planned for ${formatCookDay(cookSummary.plannedFor)}. Show times made`}
                  onClick={() => setShowCookHistory(true)}
                >
                  {formatCookDay(cookSummary.plannedFor)}
                </button>
              </div>
            )}
            {recipe.sourceType === 'url' && recipe.sourceUrl !== '' && (
              <div className="kv">
                <span>Source</span>
                <a href={recipe.sourceUrl} target="_blank" rel="noreferrer noopener">
                  {hostOf(recipe.sourceUrl)}
                </a>
              </div>
            )}
            {recipe.sourceType === 'text' && recipe.sourceText !== '' && (
              <div className="kv">
                <span>Source</span>
                <span>{recipe.sourceText}</span>
              </div>
            )}

            {recipe.importedFrom != null && (
              <p className="hint">
                Saved from {recipe.importedFrom.sourceHouseholdName}. This copy is yours to edit.
              </p>
            )}

            <div className="recipe-collections">
              <span className="label">Collections</span>
              <div className="row wrap" style={{ marginTop: 8 }}>
                {inCollections.map((collection) => (
                  <Link
                    key={collection.id}
                    className="tag"
                    to="/collections/$collectionId"
                    params={{ collectionId: collection.id }}
                  >
                    {collection.name}
                  </Link>
                ))}
              </div>
            </div>

            {recipe.tags.length > 0 && (
              <div>
                <span className="label">Tags</span>
                <div className="row wrap" style={{ marginTop: 8 }}>
                  {recipe.tags.map((tag) => (
                    <Link key={tag} className="tag" to="/" search={{ tag }}>
                      {formatTag(tag)}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </aside>

          <section className="recipe-ingredients">
            <div className="ingredients-header">
              <h2 className="section-title">Ingredients</h2>
              {recipe.ingredients.length > 0 && (
                <button
                  type="button"
                  className={`scale-toggle ${scaled ? 'scaled' : ''}`}
                  aria-expanded={scaleOpen}
                  onClick={() => setScale({ recipeId, factor: scaleFactor, open: !scaleOpen })}
                >
                  {scaled ? `Scaled ${formatScaleFactor(scaleFactor)}` : 'Scale'}
                </button>
              )}
            </div>
            {scaleOpen && (
              <div className="recipe-scale">
                <ScaleSwitch
                  factor={scaleFactor}
                  onChange={(factor) => setScale({ recipeId, factor, open: true })}
                />
                <div className="scale-status">
                  <span>
                    {scaled
                      ? [
                          `${formatScaleFactor(scaleFactor)} batch`,
                          scaledServings != null && formatServingsLabel(scaledServings),
                        ]
                          .filter(Boolean)
                          .join(' · ')
                      : 'Original amounts'}
                  </span>
                  {scaled && (
                    <button
                      type="button"
                      className="scale-reset"
                      onClick={() => setScale({ recipeId, factor: 1, open: false })}
                    >
                      Reset
                    </button>
                  )}
                </div>
              </div>
            )}
            <ul className="ingredients">
              {recipe.ingredients.map((ingredient, index) => {
                const heading =
                  ingredient.groupHeading !== '' &&
                  ingredient.groupHeading !== recipe.ingredients[index - 1]?.groupHeading;
                const amount = scaleIngredientAmount(
                  ingredient.quantity,
                  ingredient.unit,
                  scaleFactor,
                );
                return (
                  <li key={ingredient.id}>
                    {heading && <h3 className="section-title">{ingredient.groupHeading}</h3>}
                    <div className="ingredient">
                      <span className={`amount ${amount.scaled ? 'scaled' : ''}`}>
                        {amount.text}
                      </span>
                      <span>
                        {ingredient.name}
                        {ingredient.note !== '' && (
                          <em style={{ color: 'var(--ink-faint)' }}> — {ingredient.note}</em>
                        )}
                      </span>
                    </div>
                  </li>
                );
              })}
            </ul>
            {recipe.ingredients.length === 0 && <p className="hint">No ingredients listed.</p>}
          </section>

          <section className="recipe-method">
            <h2 className="section-title">Method</h2>
            <ol className="steps">
              {recipe.instructions.map((instruction) => (
                <li className="step" key={instruction.id}>
                  <span>{instruction.text}</span>
                </li>
              ))}
            </ol>
            {recipe.instructions.length === 0 && <p className="hint">No steps yet.</p>}
          </section>
        </div>

        {recipe.notes !== '' && (
          <section className="recipe-notes">
            <h2 className="section-title">Notes</h2>
            <p>{recipe.notes}</p>
          </section>
        )}

        {/* Print only. On paper the facts read better as a footnote at the end than as a
            column of rows competing with the ingredients, so the sidebar copy is hidden
            and this one takes over — see the print rules in styles.css. */}
        {recipe.nutritionFacts.length > 0 && (
          <p className="recipe-nutrition-line">
            <span className="label">Nutrition</span>{' '}
            {/* Imports default to an unstated basis, and "Nutrition Unknown — 540 kcal"
                reads as a fact about the recipe rather than a missing one. */}
            {recipe.nutritionBasis !== 'unknown' && (
              <>{nutritionBasisLabel(recipe.nutritionBasis)} — </>
            )}
            {recipe.nutritionFacts
              .map((fact) => [fact.label, formatNutritionAmount(fact)].filter(Boolean).join(' '))
              .join(' · ')}
          </p>
        )}
      </div>

      {showCookDates && (
        <CookDateDialog
          title={recipe.title}
          cookDates={recipe.cookDates}
          readOnly={readOnly}
          onSet={(date) => {
            void store.addCookDate(recipeId, date, scaleFactor);
            showToast(cookDateSavedMessage(date));
            setShowCookDates(false);
          }}
          onClear={(ids) => {
            for (const id of ids) void store.removeCookDate(recipeId, id);
          }}
          onClose={() => setShowCookDates(false)}
        />
      )}
      {showCookHistory && (
        <CookHistoryDialog
          title={recipe.title}
          cookDates={recipe.cookDates}
          onClose={() => setShowCookHistory(false)}
        />
      )}
      {showHistory && <RevisionDrawer recipe={recipe} onClose={() => setShowHistory(false)} />}
      {showShare && (
        <ShareDialog
          kind="recipe"
          title={recipe.title}
          recipeIds={[recipe.id]}
          onClose={() => setShowShare(false)}
        />
      )}
      {lightboxIndex != null && recipe.photos.length > 0 && (
        <PhotoLightbox
          photos={recipe.photos}
          initialIndex={lightboxIndex}
          alt={recipe.title}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </>
  );
}

function formatNutritionAmount(fact: NutritionFact): string {
  return [fact.amount, fact.unit].filter(Boolean).join(' ');
}

function hostOf(url: string): string {
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
}
